import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import {
  COOKIE_PREFERENCES_EVENT,
  getConsent,
  hasConsentDecision,
  setConsent,
} from '../../lib/consent';

const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

/**
 * Consent GDPR: categorie (necessary/analytics/marketing), accept-all/reject-all,
 * pannello gestione con salvataggio, persistenza versionata e riapertura globale.
 * Nessun overlay a schermo intero: card dismissibile in basso, theme-aware.
 */
export function CookieConsent() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(() => !hasConsentDecision());
  const [panelOpen, setPanelOpen] = useState(false);
  const [analytics, setAnalytics] = useState(() => getConsent()?.categories.analytics ?? false);
  const [marketing, setMarketing] = useState(() => getConsent()?.categories.marketing ?? false);
  const cardRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const openPreferences = () => {
      const state = getConsent();
      setAnalytics(state?.categories.analytics ?? false);
      setMarketing(state?.categories.marketing ?? false);
      setVisible(true);
      setPanelOpen(true);
    };
    window.addEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
  }, []);

  // Focus management: all'apertura del pannello il focus entra nel pannello stesso.
  useEffect(() => {
    if (!visible || !panelOpen) return;
    const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    first?.focus();
  }, [visible, panelOpen]);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    setPanelOpen(false);
  };
  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    close();
  };
  const rejectAll = () => {
    setConsent({ analytics: false, marketing: false });
    close();
  };
  const savePreferences = () => {
    setConsent({ analytics, marketing });
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (panelOpen) setPanelOpen(false);
      else close();
      return;
    }
    // Focus trap: Tab/Shift+Tab restano dentro il dialog.
    if (e.key === 'Tab' && cardRef.current) {
      const focusables = Array.from(cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const toggleClass = 'h-5 w-5 shrink-0 accent-brand-interactive cursor-pointer';

  return (
    // `bottom` segue `--jarvis-bottom-chrome` (altezza del footer della Guida, impostata da
    // PrompterChat): su mobile il banner copriva la barra comandi e ne intercettava i click
    // (misurato: la spec non riusciva a cliccare i comandi). Nessuna dipendenza dal contesto Guida.
    <div
      className="fixed left-0 right-0 p-4 md:p-6 z-[9990] pointer-events-none"
      style={{ bottom: 'var(--jarvis-bottom-chrome, 0px)' }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('consent', 'title')}
        data-non-blocking-dialog
        onKeyDown={onKeyDown}
        className="max-w-4xl mx-auto bg-surface-card/95 backdrop-blur-xl border border-border-subtle p-5 md:p-6 rounded-2xl shadow-[var(--shadow-premium)] pointer-events-auto animate-in slide-in-from-bottom-10 fade-in"
      >
        <div className="flex flex-col gap-4">
          <div className="text-sm md:text-base leading-relaxed">
            <p className="font-semibold mb-1 text-text-primary">{t('consent', 'title')}</p>
            <p className="text-text-muted text-xs md:text-sm">{t('consent', 'description')}</p>
            <p className="text-text-muted text-xs md:text-sm mt-2 flex flex-wrap gap-x-2 gap-y-1">
              <Link to="/privacy" className="text-brand-interactive underline hover:no-underline">
                {t('consent', 'privacy_link')}
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/terms" className="text-brand-interactive underline hover:no-underline">
                {t('consent', 'terms_link')}
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/cookies" className="text-brand-interactive underline hover:no-underline">
                {t('consent', 'cookies_link')}
              </Link>
            </p>
          </div>

          {panelOpen && (
            <div ref={panelRef} className="flex flex-col gap-3 border-t border-border-subtle pt-4">
              <div className="flex items-start gap-3">
                <input id="consent-necessary" type="checkbox" checked disabled className={toggleClass} />
                <label htmlFor="consent-necessary" className="text-xs md:text-sm cursor-pointer">
                  <span className="font-semibold text-text-primary block">
                    {t('consent', 'cat_necessary')}
                    <span className="ml-2 text-text-muted font-normal">({t('consent', 'always_active')})</span>
                  </span>
                  <span className="text-text-muted block">{t('consent', 'cat_necessary_desc')}</span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="consent-analytics"
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className={toggleClass}
                />
                <label htmlFor="consent-analytics" className="text-xs md:text-sm cursor-pointer">
                  <span className="font-semibold text-text-primary block">{t('consent', 'cat_analytics')}</span>
                  <span className="text-text-muted block">{t('consent', 'cat_analytics_desc')}</span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="consent-marketing"
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className={toggleClass}
                />
                <label htmlFor="consent-marketing" className="text-xs md:text-sm cursor-pointer">
                  <span className="font-semibold text-text-primary block">{t('consent', 'cat_marketing')}</span>
                  <span className="text-text-muted block">{t('consent', 'cat_marketing_desc')}</span>
                </label>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
            {!panelOpen && (
              <button
                onClick={() => setPanelOpen(true)}
                className="px-4 py-2.5 border border-border-subtle text-text-primary rounded-xl font-semibold hover:bg-surface-hover transition-colors active:scale-95"
              >
                {t('consent', 'manage')}
              </button>
            )}
            <button
              onClick={rejectAll}
              className="px-4 py-2.5 border border-border-subtle text-text-primary rounded-xl font-semibold hover:bg-surface-hover transition-colors active:scale-95"
            >
              {t('consent', 'reject_all')}
            </button>
            {panelOpen ? (
              <button
                onClick={savePreferences}
                className="px-6 py-2.5 bg-brand-interactive text-brand-contrast-text rounded-xl font-bold transition-all shadow-lg hover:shadow-[var(--shadow-premium)] active:scale-95"
              >
                {t('consent', 'save')}
              </button>
            ) : (
              <button
                onClick={acceptAll}
                className="px-6 py-2.5 bg-brand-interactive text-brand-contrast-text rounded-xl font-bold transition-all shadow-lg hover:shadow-[var(--shadow-premium)] active:scale-95"
              >
                {t('consent', 'accept_all')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
